/**
 * 元素插入目标：当前激活页（页内坐标）或无激活页时的工作区（世界坐标视口中心）。
 */
import type { Page } from '@/components/canvas/document/publicTypes';
import { getViewportWorldBounds } from '@/components/canvas/page/pageBounds';

export type ElementInsertScope = 'page' | 'workspace';

/** 文本框绘制落点：页内局部坐标 vs 工作区世界坐标 */
export type TextInsertVia = 'page' | 'workspace';

export interface ElementInsertContext {
  scope: ElementInsertScope;
  page: Page | null;
  /** 页内局部或世界坐标锚点（元素居中时减去半宽/半高） */
  anchorX: number;
  anchorY: number;
}

export function getWorkspaceShellSize(): { width: number; height: number } {
  const el = document.querySelector('.workspace') as HTMLElement | null;
  if (el && el.clientWidth > 0 && el.clientHeight > 0) {
    return { width: el.clientWidth, height: el.clientHeight };
  }
  return { width: 1200, height: 800 };
}

export function resolveElementInsertContext(
  pages: Page[],
  currentPageId: string,
  panX: number,
  panY: number,
  zoom: number,
): ElementInsertContext {
  const page = currentPageId ? pages.find((p) => p.id === currentPageId) ?? null : null;
  if (page) {
    return {
      scope: 'page',
      page,
      anchorX: page.size.width / 2,
      anchorY: page.size.height / 2,
    };
  }
  const { width, height } = getWorkspaceShellSize();
  const vb = getViewportWorldBounds(width, height, panX, panY, zoom);
  return {
    scope: 'workspace',
    page: null,
    anchorX: vb.x + vb.width / 2,
    anchorY: vb.y + vb.height / 2,
  };
}

/** 文本绘制插入：按落点决定 page / workspace，与 currentPageId 解耦 */
export function resolveTextInsertContext(
  pages: Page[],
  currentPageId: string,
  coords: { x: number; y: number },
  via: TextInsertVia,
  pageId?: string,
): ElementInsertContext {
  if (via === 'workspace') {
    return { scope: 'workspace', page: null, anchorX: coords.x, anchorY: coords.y };
  }
  const pid = pageId ?? currentPageId;
  const page = pid ? pages.find((p) => p.id === pid) ?? null : null;
  if (!page) {
    return { scope: 'workspace', page: null, anchorX: coords.x, anchorY: coords.y };
  }
  return { scope: 'page', page, anchorX: coords.x, anchorY: coords.y };
}

/** 插入文本后同步激活目标：落页激活该页，落工作区取消激活页 */
export function textInsertActivationPatch(ctx: ElementInsertContext) {
  if (ctx.scope === 'workspace') {
    return {
      currentPageId: '' as const,
      isPageSelected: false,
      selectedPageIds: [] as string[],
      editingTextId: null,
      editingGroupId: null,
    };
  }
  if (ctx.page) {
    return {
      currentPageId: ctx.page.id,
      isPageSelected: false,
      selectedPageIds: [] as string[],
    };
  }
  return {};
}

/** 将元素中心对齐到锚点 */
export function layoutCenteredOnAnchor(
  anchorX: number,
  anchorY: number,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: Math.round(anchorX - width / 2),
    y: Math.round(anchorY - height / 2),
  };
}
